import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { eur, fmtTime, fmtDate, initials, romeMinutes, romeDayKey } from "@/lib/format";
import { APPT_STATUS, OP_STATUS, OP_STATUS_ORDER } from "@/lib/constants";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { WaitlistManager } from "@/components/agenda/WaitlistManager";
import { ColorFormulaPanel } from "@/components/common/ColorFormulaPanel";
import { OperatorDrawer } from "@/components/agenda/OperatorDrawer";
import { ClientSearch } from "@/components/common/ClientSearch";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, Loader2, Wallet, X, Timer, Lock, Clock3, CalendarDays, ShieldCheck, Pencil, Users, AlertTriangle } from "lucide-react";

const HOURS = Array.from({ length: 24 }, (_, i) => i); // 00..23 (Agenda H24)
const PX_PER_MIN = 1.1;
const DAY_START = 0; // mezzanotte: agenda visibile dalle 00:00 alle 24:00

// Calcola le "lane" (colonne affiancate) per gli appuntamenti che si sovrappongono
// nello stesso operatore, così da renderli tutti visibili e ordinati.
// Ritorna una Map: appointmentId -> { lane, lanes }.
function computeLanes(list) {
  const map = new Map();
  const evs = list.map((a) => {
    const s = romeMinutes(a.start);
    return { a, s, e: s + (a.duration_min || 30) };
  }).sort((x, y) => (x.s - y.s) || (x.e - y.e));

  let cluster = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    if (!cluster.length) return;
    const colEnds = []; // fine dell'ultimo appuntamento per ciascuna colonna
    cluster.forEach((item) => {
      let placed = false;
      for (let ci = 0; ci < colEnds.length; ci++) {
        if (colEnds[ci] <= item.s) { item.col = ci; colEnds[ci] = item.e; placed = true; break; }
      }
      if (!placed) { item.col = colEnds.length; colEnds.push(item.e); }
    });
    const lanes = colEnds.length;
    cluster.forEach((item) => map.set(item.a.id, { lane: item.col, lanes }));
    cluster = [];
  };

  evs.forEach((item) => {
    if (cluster.length && item.s >= clusterEnd) { flush(); clusterEnd = -Infinity; }
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.e);
  });
  flush();
  return map;
}

// Colore per categoria servizio = FONTE UNICA condivisa con la Cassa.
// Viene popolata a runtime dalle categorie configurate (/service-categories),
// così cambiando un colore si aggiornano sia Cassa sia Agenda senza toccare i servizi.
let CAT_COLOR_MAP = {};
export function setCatColorMap(map) { CAT_COLOR_MAP = map || {}; }
// Tinte di riserva per categorie non mappate (assegnazione deterministica).
const FALLBACK_HUES = [
  "14 80% 52%", "173 58% 39%", "48 90% 46%", "262 65% 58%",
  "0 72% 55%", "191 82% 40%", "128 55% 42%", "310 60% 52%",
];
function apptCategory(a, svcCat) {
  if (a.resolved_lines && a.resolved_lines.length && a.resolved_lines[0].category) return a.resolved_lines[0].category;
  const sid = (a.service_ids || [])[0];
  return sid && svcCat ? svcCat[sid] : null;
}
function categoryColor(cat) {
  if (cat && CAT_COLOR_MAP[cat]) return CAT_COLOR_MAP[cat];
  if (!cat) return "43 45% 54%"; // oro (default neutro)
  let h = 0;
  for (let i = 0; i < cat.length; i++) h = (h * 31 + cat.charCodeAt(i)) >>> 0;
  return FALLBACK_HUES[h % FALLBACK_HUES.length];
}



// Formatta una Date come stringa locale YYYY-MM-DD (per input type="date").
const toDateStr = (d) => { const off = d.getTimezoneOffset() * 60000; return new Date(d.getTime() - off).toISOString().slice(0, 10); };

export default function Agenda() {
  const navigate = useNavigate();
  const [view, setView] = useState("day");
  const [date, setDate] = useState(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkRef = useRef(null);
  // Deep-link dalla notifica: ?date=YYYY-MM-DD&appt=ID -> vai al giorno e apri l'appuntamento
  useEffect(() => {
    const d = searchParams.get("date");
    if (d) { const nd = new Date(d + "T12:00:00"); if (!isNaN(nd.getTime())) { setView("day"); setDate(nd); } }
    const aid = searchParams.get("appt");
    if (aid) deepLinkRef.current = aid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (deepLinkRef.current && appts && appts.length) {
      const found = appts.find((a) => a.id === deepLinkRef.current);
      if (found) {
        setSelected(found);
        deepLinkRef.current = null;
        const p = new URLSearchParams(searchParams); p.delete("appt"); p.delete("date"); setSearchParams(p, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appts]);
  const [newClient, setNewClient] = useState(null);
  const [changingClient, setChangingClient] = useState(false);
  const [assignEditing, setAssignEditing] = useState(null); // key servizio#unit in assegnazione
  // Consensi PERMANENTI della cliente (letti dalla scheda cliente, non dall'appuntamento)
  const [clientConsents, setClientConsents] = useState(null);
  const [consentEditing, setConsentEditing] = useState(false);
  const [consentDraft, setConsentDraft] = useState({ privacy_delivered: false, marketing: "unset", acquisition_method: "" });
  const [consentSaving, setConsentSaving] = useState(false);
  const [newForm, setNewForm] = useState({ client_id: "", operator_id: "", service_ids: [], service_qty: {}, expected_price: "", time: "10:00", notes: "", date: toDateStr(new Date()) });
  // Deep-link dal profilo cliente: ?client=ID -> apri "Nuovo appuntamento" con la cliente preselezionata
  useEffect(() => {
    const cid = searchParams.get("client");
    if (!cid) return;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${cid}`);
        const cl = data.client || data;
        if (cl && cl.id) {
          setNewClient(cl);
          setNewForm((f) => ({ ...f, client_id: cl.id }));
          setOpenNew(true);
        }
      } catch (e) { /* cliente non trovata: apri comunque il pannello vuoto */ setOpenNew(true); }
      const p = new URLSearchParams(searchParams); p.delete("client"); setSearchParams(p, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editForm, setEditForm] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payLines, setPayLines] = useState([]);
  const [payments, setPayments] = useState([{ method: "Contanti", amount: "" }]);
  const [payBusy, setPayBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [opDrawer, setOpDrawer] = useState({ open: false, operatorId: null });
  const [availability, setAvailability] = useState(null);
  useEffect(() => {
    if (view !== "day") { return; }
    const ds = romeDayKey(date);
    api.get(`/agenda/availability?date=${ds}`).then((r) => setAvailability(r.data)).catch(() => setAvailability(null));
  }, [date, view]);
  const openOperator = (operatorId) => setOpDrawer({ open: true, operatorId });
  const selectApptById = (id) => { const a = appts.find((x) => x.id === id); if (a) { setOpDrawer((d) => ({ ...d, open: false })); setSelected(a); } };
  const alertedRef = useRef(new Set());
  useEffect(() => {
    if (selected) setEditForm({ date: romeDayKey(selected.start), time: fmtTime(selected.start), operator_id: selected.operator_id, service_ids: selected.service_ids || [], service_qty: selected.service_qty || Object.fromEntries((selected.resolved_lines || []).map((l) => [l.service_id, l.quantity || 1])), expected_price: selected.expected_price != null ? String(selected.expected_price) : "", notes: selected.notes || "" });
    else setEditForm(null);
  }, [selected]);

  const range = useMemo(() => {
    const s = new Date(date); s.setHours(0, 0, 0, 0);
    const e = new Date(date);
    if (view === "day") e.setHours(23, 59, 59, 999);
    else if (view === "week") { s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); e.setTime(s.getTime()); e.setDate(s.getDate() + 6); e.setHours(23,59,59,999); }
    else { s.setDate(1); e.setMonth(s.getMonth() + 1, 0); e.setHours(23,59,59,999); }
    return { s, e };
  }, [date, view]);

  const svcCatById = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s.category])), [services]);
  const [, setCatVersion] = useState(0); // forza il re-render quando arrivano i colori categoria

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/appointments?start=${range.s.toISOString()}&end=${range.e.toISOString()}`)
      .then((r) => setAppts(r.data)).finally(() => setLoading(false));
  }, [range]);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data.filter((t) => (
      t.bookable_internal !== undefined
        ? t.bookable_internal === true
        : ["stylist", "assistant", "manager"].includes(t.role)
    )))).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    // Categorie = fonte unica colore (condivisa con Cassa). Popola la mappa colore.
    api.get("/service-categories").then((r) => {
      const cats = Array.isArray(r.data) ? r.data : [];
      const map = {}; cats.forEach((c) => { map[c.name] = c.color; });
      setCatColorMap(map);
      setCatVersion((v) => v + 1);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Mappa insoluti aperti per cliente (indicatore in agenda)
  const [debtSet, setDebtSet] = useState(() => new Set());
  useEffect(() => {
    api.get("/debts/open-ids").then((r) => setDebtSet(new Set(Object.keys(r.data?.clients || {})))).catch(() => {});
  }, [appts]);

  const shift = (n) => { const d = new Date(date); d.setDate(d.getDate() + n * (view === "month" ? 30 : view === "week" ? 7 : 1)); setDate(d); };

  const createAppt = async () => {
    if (!newForm.client_id || !newForm.operator_id || newForm.service_ids.length === 0) { toast.error("Compila cliente, operatore e servizi"); return; }
    if (!newForm.date) { toast.error("Seleziona il giorno"); return; }
    const start = new Date(`${newForm.date}T${newForm.time}:00`);
    if (isNaN(start.getTime())) { toast.error("Giorno o orario non valido"); return; }
    try {
      await api.post("/appointments", { client_id: newForm.client_id, operator_id: newForm.operator_id, service_ids: newForm.service_ids, service_qty: newForm.service_qty, expected_price: newForm.expected_price !== "" ? Number(newForm.expected_price) : null, start: start.toISOString(), notes: newForm.notes });
      toast.success("Appuntamento creato");
      setOpenNew(false); setNewForm({ client_id: "", operator_id: "", service_ids: [], service_qty: {}, expected_price: "", time: "10:00", notes: "", date: toDateStr(date) }); setNewClient(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // Consensi cliente: leggi lo stato PERMANENTE quando cambia la cliente selezionata
  const loadConsents = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/clients/${cid}/consents`);
      setClientConsents(data);
      setConsentDraft({
        privacy_delivered: !!data?.privacy?.delivered,
        marketing: data?.marketing?.status || "unset",
        acquisition_method: data?.marketing?.source || "",
      });
      // Se già completo (informativa + scelta marketing) mostra lo stato, altrimenti abilita l'editing
      setConsentEditing(!data?.complete);
    } catch (e) {
      setClientConsents(null); setConsentEditing(false);
    }
  }, []);
  useEffect(() => {
    if (newClient?.id) loadConsents(newClient.id);
    else { setClientConsents(null); setConsentEditing(false); setConsentDraft({ privacy_delivered: false, marketing: "unset", acquisition_method: "" }); }
  }, [newClient?.id, loadConsents]);

  const saveConsents = async () => {
    if (!newClient?.id) return;
    setConsentSaving(true);
    try {
      const payload = {};
      if (consentDraft.privacy_delivered) payload.privacy_delivered = true;
      if (consentDraft.marketing === "granted" || consentDraft.marketing === "denied") {
        payload.marketing = consentDraft.marketing;
        if (consentDraft.marketing === "granted") payload.acquisition_method = consentDraft.acquisition_method || null;
      }
      const { data } = await api.put(`/clients/${newClient.id}/consents`, payload);
      setClientConsents(data);
      setConsentEditing(!data?.complete ? true : false);
      toast.success("Consensi salvati sulla scheda cliente");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio consensi"); }
    finally { setConsentSaving(false); }
  };


  const changeStatus = async (a, status) => {    await api.put(`/appointments/${a.id}/status`, { status });
    toast.success("Stato aggiornato");
    setSelected({ ...a, status }); load();
  };
  const changeOpStatus = async (a, op) => {
    setSelected((s) => ({ ...s, op_status: op }));
    setAppts((prev) => prev.map((x) => x.id === a.id ? { ...x, op_status: op } : x));
    try { await api.put(`/appointments/${a.id}/op-status`, { op_status: op }); toast.success(`Avanzamento: ${OP_STATUS[op]?.label || op}`); load(); }
    catch (e) { toast.error("Errore avanzamento"); load(); }
  };
  const removeAppt = async (a) => { await api.delete(`/appointments/${a.id}`); toast.success("Eliminato"); setSelected(null); load(); };

  const saveEdit = async () => {
    if (!editForm || !selected) return;
    if (!editForm.operator_id || (editForm.service_ids || []).length === 0) { toast.error("Seleziona operatore e almeno un servizio"); return; }
    const start = new Date(`${editForm.date}T${editForm.time}:00`).toISOString();
    try {
      await api.put(`/appointments/${selected.id}`, { start, operator_id: editForm.operator_id, service_ids: editForm.service_ids, service_qty: editForm.service_qty, expected_price: editForm.expected_price !== "" ? Number(editForm.expected_price) : null, notes: editForm.notes });
      toast.success("Appuntamento aggiornato");
      setSelected(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento"); }
  };

  // Costruisce l'istante di inizio (Europe/Rome, browser locale del salone) per il giorno visualizzato.
  const startISOForTime = (hhmm) => { const [h, m] = hhmm.split(":"); const d = new Date(date); d.setHours(+h, +m, 0, 0); return d.toISOString(); };

  const quickCreate = (operatorId, hhmm) => {
    setNewClient(null);
    setNewForm({ client_id: "", operator_id: operatorId, service_ids: [], service_qty: {}, expected_price: "", time: hhmm, notes: "", date: toDateStr(date) });
    setOpenNew(true);
  };

  const moveAppt = async (apptId, operatorId, hhmm) => {
    const a = appts.find((x) => x.id === apptId);
    if (!a) return;
    // Ottimistico: aggiorna subito la UI
    setAppts((prev) => prev.map((x) => x.id === apptId ? { ...x, operator_id: operatorId, start: startISOForTime(hhmm) } : x));
    try {
      await api.put(`/appointments/${apptId}`, { operator_id: operatorId, start: startISOForTime(hhmm) });
      toast.success(`Spostato alle ${hhmm}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore spostamento"); load(); }
  };

  const changeClient = async (c) => {
    try {
      await api.put(`/appointments/${selected.id}`, { client_id: c.id });
      toast.success("Cliente aggiornata");
      setSelected((s) => ({ ...s, client_id: c.id, client_name: `${c.first_name} ${c.last_name}`.trim() }));
      setChangingClient(false);
      load();
    } catch (e) { toast.error("Errore aggiornamento cliente"); }
  };

  // Assegnazione di una singola istanza di servizio a un'altra cliente (es. la figlia
  // che arriva con la mamma): NON ricrea l'appuntamento e NON cambia orario/dati.
  // Il fatturato resta all'intestatario dell'appuntamento; è un riferimento operativo.
  const getAssignment = (sid, unit) => (selected?.item_assignments || []).find((a) => a.service_id === sid && a.unit === unit) || null;
  const setAssignment = async (sid, unit, client) => {
    if (!selected) return;
    const others = (selected.item_assignments || []).filter((a) => !(a.service_id === sid && a.unit === unit));
    const next = client
      ? [...others, { service_id: sid, unit, client_id: client.id, client_name: `${client.first_name} ${client.last_name}`.trim() }]
      : others;
    try {
      await api.put(`/appointments/${selected.id}`, { item_assignments: next });
      setSelected((s) => ({ ...s, item_assignments: next }));
      setAssignEditing(null);
      toast.success(client ? "Servizio assegnato all'altra cliente" : "Assegnazione rimossa");
    } catch (e) { toast.error("Errore assegnazione servizio"); }
  };

  // Fase 5.2.4 — Checkout da appuntamento: costruisce le righe carrello
  const buildApptLines = (a) => {
    const svcById = Object.fromEntries(services.map((s) => [s.id, s]));
    if (a.resolved_lines && a.resolved_lines.length) {
      return a.resolved_lines.map((l) => ({
        service_id: l.service_id, name: l.name,
        unit_price: l.unit_price ?? svcById[l.service_id]?.price ?? 0,
        quantity: l.quantity || 1, category: l.category,
      }));
    }
    return (a.service_ids || []).map((sid, i) => {
      const s = svcById[sid];
      return { service_id: sid, name: (a.service_names || [])[i] || s?.name || "Servizio",
               unit_price: s?.price ?? 0, quantity: 1, category: s?.category };
    });
  };

  const goCheckout = (a) => {
    navigate("/cassa", { state: { checkoutAppointment: {
      id: a.id, client_id: a.client_id, client_name: a.client_name,
      operator_id: a.operator_id, lines: buildApptLines(a),
    } } });
  };

  // Fase 16 — Pagamento rapido direttamente dall'appuntamento (senza lasciare l'agenda)
  const PAY_METHODS = ["Contanti", "Carta", "Bancomat", "Gift Card", "Credito", "Bonifico"];
  const openQuickPay = (a) => {
    const lines = buildApptLines(a);
    setPayLines(lines);
    const tot = lines.reduce((s, l) => s + (Number(l.unit_price) || 0) * (l.quantity || 1), 0);
    setPayments([{ method: "Contanti", amount: tot ? tot.toFixed(2) : "" }]);
    setPayOpen(true);
  };
  const payTotal = payLines.reduce((s, l) => s + (Number(l.unit_price) || 0) * (l.quantity || 1), 0);
  const paySum = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const submitQuickPay = async (unpaid = false) => {
    if (!selected) return;
    setPayBusy(true);
    try {
      const pays = unpaid ? [] : payments.filter((p) => Number(p.amount) > 0);
      await api.post("/sales", {
        client_id: selected.client_id || null, operator_id: selected.operator_id || null,
        appointment_id: selected.id, lines: payLines.map((l) => ({ ...l, operator_id: selected.operator_id || l.operator_id })),
        payments: pays,
      });
      toast.success(unpaid ? "Vendita salvata (non pagata)" : "Pagamento registrato");
      setPayOpen(false); setSelected(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore pagamento"); }
    finally { setPayBusy(false); }
  };

  // Fase 16 — Timer servizio (posa/decolorazione): tick per countdown + avviso a fine tempo
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const now = Date.now();
    for (const a of appts) {
      for (const t of (a.timers || [])) {
        const end = new Date(t.ends_at).getTime();
        if (end <= now && !alertedRef.current.has(t.id)) {
          alertedRef.current.add(t.id);
          toast.warning(`Timer terminato: ${t.label} · ${a.client_name}`, { duration: 10000 });
          if ("Notification" in window && Notification.permission === "granted") {
            try { new Notification("Timer terminato", { body: `${t.label} · ${a.client_name}` }); } catch (e) {}
          }
        }
      }
    }
  }, [tick, appts]);
  const TIMER_PRESETS = [{ label: "Posa", minutes: 20 }, { label: "Decolorazione", minutes: 30 }, { label: "Colore", minutes: 35 }];
  const startTimer = async (a, label, minutes) => {
    try {
      const { data } = await api.post(`/appointments/${a.id}/timer`, { label, minutes });
      setSelected((s) => s && s.id === a.id ? { ...s, timers: data.timers } : s);
      setAppts((prev) => prev.map((x) => x.id === a.id ? { ...x, timers: data.timers } : x));
      toast.success(`Timer avviato: ${label} ${minutes}'`);
      if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
    } catch (e) { toast.error("Errore avvio timer"); }
  };
  const removeTimer = async (a, tid) => {
    try {
      const { data } = await api.delete(`/appointments/${a.id}/timer/${tid}`);
      setSelected((s) => s && s.id === a.id ? { ...s, timers: data.timers } : s);
      setAppts((prev) => prev.map((x) => x.id === a.id ? { ...x, timers: data.timers } : x));
    } catch (e) { toast.error("Errore"); }
  };

  // Selezione servizi con QUANTITÀ (es. Piega ×2). Ogni unità conta separatamente
  // in prezzo, durata, cassa, report e commissioni (il backend risolve le quantità).
  const toggleService = (id) => setNewForm((f) => {
    const has = f.service_ids.includes(id);
    const ids = has ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id];
    const q = { ...(f.service_qty || {}) };
    if (has) delete q[id]; else q[id] = 1;
    return { ...f, service_ids: ids, service_qty: q };
  });
  const setNewQty = (id, val) => setNewForm((f) => ({ ...f, service_qty: { ...(f.service_qty || {}), [id]: Math.max(1, parseInt(val, 10) || 1) } }));

  const toggleEditService = (id) => setEditForm((f) => {
    const has = (f.service_ids || []).includes(id);
    const ids = has ? f.service_ids.filter((x) => x !== id) : [...(f.service_ids || []), id];
    const q = { ...(f.service_qty || {}) };
    if (has) delete q[id]; else q[id] = 1;
    return { ...f, service_ids: ids, service_qty: q };
  });
  const setEditQty = (id, val) => setEditForm((f) => ({ ...f, service_qty: { ...(f.service_qty || {}), [id]: Math.max(1, parseInt(val, 10) || 1) } }));

  return (
    <div>
      <PageHeader title="Agenda" subtitle="Gestione appuntamenti per collaboratore">
        <div className="flex items-center gap-2">
          <WaitlistManager services={services} team={team} onApproved={load} />
          <Button onClick={() => { setNewClient(null); setNewForm({ client_id: "", operator_id: "", service_ids: [], service_qty: {}, expected_price: "", time: "10:00", notes: "", date: toDateStr(date) }); setOpenNew(true); }} data-testid="agenda-new-appointment-button"><Plus className="h-4 w-4 mr-1" /> Nuovo</Button>
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="agenda-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setDate(new Date())} data-testid="agenda-today">Oggi</Button>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="agenda-next"><ChevronRight className="h-4 w-4" /></Button>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <button type="button" data-testid="agenda-date-trigger"
                className="ml-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-heading text-lg font-semibold capitalize hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hi-gold))]"
                title="Scegli una data">
                {fmtDate(date, view === "month" ? { month: "long", year: "numeric" } : { weekday: "long", day: "2-digit", month: "long" })}
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" data-testid="agenda-date-popover">
              <Calendar mode="single" selected={date} defaultMonth={date}
                onSelect={(d) => { if (d) { setDate(d); setDatePickerOpen(false); } }} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="agenda-view-toggle">
            <TabsTrigger value="day">Giorno</TabsTrigger>
            <TabsTrigger value="week">Settimana</TabsTrigger>
            <TabsTrigger value="month">Mese</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--hi-gold))]" /></div> : (
        <>
          {view === "day" && <DayView team={team} appts={appts} onSelect={setSelected} date={date} now={now} onQuickCreate={quickCreate} onMoveAppt={moveAppt} onOperatorClick={openOperator} availability={availability} svcCat={svcCatById} debtSet={debtSet} />}
          {view === "week" && <WeekView range={range} appts={appts} onSelect={setSelected} now={now} svcCat={svcCatById} debtSet={debtSet} />}
          {view === "month" && <MonthView date={date} appts={appts} onSelect={setSelected} svcCat={svcCatById} debtSet={debtSet} />}
        </>
      )}

      {/* New appointment sheet */}
      <Sheet open={openNew} onOpenChange={(o) => { setOpenNew(o); if (!o) { setNewClient(null); setNewForm({ client_id: "", operator_id: "", service_ids: [], service_qty: {}, expected_price: "", time: "10:00", notes: "", date: toDateStr(date) }); } }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-md">
          <SheetHeader><SheetTitle className="font-heading text-2xl">Nuovo appuntamento</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-1.5"><Label>Cliente</Label>
              <ClientSearch value={newClient} onSelect={(c) => { setNewClient(c); setNewForm((f) => ({ ...f, client_id: c ? c.id : "" })); }} />
            </div>
            <div className="space-y-1.5"><Label>Operatore</Label>
              <Select value={newForm.operator_id} onValueChange={(v) => setNewForm((f) => ({ ...f, operator_id: v }))}>
                <SelectTrigger data-testid="new-appt-operator"><SelectValue placeholder="Seleziona operatore" /></SelectTrigger>
                <SelectContent>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Giorno</Label>
                <Input type="date" value={newForm.date} onChange={(e) => setNewForm((f) => ({ ...f, date: e.target.value }))} data-testid="new-appt-date" />
              </div>
              <div className="space-y-1.5"><Label>Orario</Label>
                <Input type="time" value={newForm.time} onChange={(e) => setNewForm((f) => ({ ...f, time: e.target.value }))} data-testid="new-appt-time" />
              </div>
            </div>
            <div className="space-y-1.5"><Label>Servizi</Label>
              <div className="max-h-52 overflow-y-auto rounded-lg border border-border p-2 space-y-1 hi-scroll">
                {services.map((s) => {
                  const on = newForm.service_ids.includes(s.id);
                  const qty = newForm.service_qty?.[s.id] || 1;
                  return (
                    <div key={s.id} data-testid={`svc-row-${s.id}`}
                      className={`w-full flex justify-between items-center gap-2 text-sm rounded px-2 py-1.5 ${on ? "bg-[hsl(var(--hi-gold-soft))]" : "hover:bg-accent"}`}>
                      <button type="button" onClick={() => toggleService(s.id)} data-testid={`svc-opt-${s.id}`} className="flex-1 flex justify-between items-center text-left min-w-0">
                        <span className="truncate">{s.name}</span><span className="text-muted-foreground ml-2 shrink-0">{s.price_from ? "da " : ""}{eur(s.price)}</span>
                      </button>
                      {on && (
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <span className="text-xs text-muted-foreground">Qtà</span>
                          <Input type="number" min="1" value={qty} onChange={(e) => setNewQty(s.id, e.target.value)}
                            className="w-14 h-7 text-center px-1" data-testid={`svc-qty-${s.id}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Prezzo previsto <span className="text-muted-foreground font-normal">(facoltativo)</span></Label>
              <Input type="number" min="0" step="0.01" value={newForm.expected_price} onChange={(e) => setNewForm((f) => ({ ...f, expected_price: e.target.value }))} placeholder="Per servizi a prezzo variabile" data-testid="new-appt-expected-price" />
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea value={newForm.notes} onChange={(e) => setNewForm((f) => ({ ...f, notes: e.target.value }))} /></div>

            {/* Consensi & Privacy — PERMANENTI sulla scheda cliente (l'appuntamento li legge) */}
            <div className="rounded-xl border border-border p-3 space-y-3" data-testid="new-appt-consent-block">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
                  <span className="text-sm font-semibold">Consensi e privacy</span>
                </div>
                {clientConsents?.complete && !consentEditing && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setConsentEditing(true)} data-testid="consent-edit-button">Modifica consensi</Button>
                )}
              </div>

              {!newClient ? (
                <p className="text-[11px] text-muted-foreground" data-testid="consent-no-client">Seleziona una cliente per gestire i consensi.</p>
              ) : (clientConsents?.complete && !consentEditing) ? (
                /* STATO informativo (già espressi): nessuna casella da compilare */
                <div className="space-y-1.5 text-sm" data-testid="consent-status-view">
                  <div className="flex items-center gap-2" data-testid="consent-status-privacy">
                    <span className="text-[hsl(var(--hi-status-success))]">✅</span>
                    <span>Informativa privacy registrata{clientConsents.privacy.at ? ` — ${new Date(clientConsents.privacy.at).toLocaleDateString("it-IT")}` : ""}</span>
                  </div>
                  <div className="flex items-center gap-2" data-testid="consent-status-marketing">
                    {clientConsents.marketing.status === "granted"
                      ? <><span className="text-[hsl(var(--hi-status-success))]">✅</span><span>Comunicazioni promozionali autorizzate</span></>
                      : <><span className="text-[hsl(var(--hi-status-danger))]">❌</span><span>Comunicazioni promozionali non autorizzate</span></>}
                  </div>
                  {clientConsents.privacy.needs_reconsent && (
                    <p className="text-[11px] text-[hsl(var(--hi-status-warning))]">Nuova versione dell'informativa disponibile: valuta una nuova presa visione.</p>
                  )}
                </div>
              ) : (
                /* EDITING: prima registrazione oppure modifica volontaria */
                <div className="space-y-3" data-testid="consent-edit-view">
                  <label className="flex items-start gap-3 cursor-pointer" htmlFor="appt-privacy-delivered">
                    <Checkbox id="appt-privacy-delivered" checked={!!consentDraft.privacy_delivered} onCheckedChange={(v) => setConsentDraft((d) => ({ ...d, privacy_delivered: !!v }))} className="mt-0.5" data-testid="new-appt-privacy-checkbox" />
                    <span className="text-sm leading-relaxed">Informativa privacy consegnata/comunicata alla cliente.</span>
                  </label>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Consenso comunicazioni promozionali (email/WhatsApp)</Label>
                    <Select value={consentDraft.marketing} onValueChange={(v) => setConsentDraft((d) => ({ ...d, marketing: v, acquisition_method: v === "granted" ? d.acquisition_method : "" }))}>
                      <SelectTrigger data-testid="new-appt-marketing-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="granted">Consentito</SelectItem>
                        <SelectItem value="denied">Negato</SelectItem>
                        <SelectItem value="unset">Non ancora espresso</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">Attivare "Consentito" solo se realmente dichiarato dalla cliente. Non attribuire automaticamente il consenso inserendo email o telefono.</p>
                  </div>

                  {consentDraft.marketing === "granted" && (
                    <div className="space-y-1.5" data-testid="new-appt-acquisition-wrap">
                      <Label className="text-xs">Modalità di acquisizione del consenso</Label>
                      <Select value={consentDraft.acquisition_method || ""} onValueChange={(v) => setConsentDraft((d) => ({ ...d, acquisition_method: v }))}>
                        <SelectTrigger data-testid="new-appt-acquisition-method"><SelectValue placeholder="Seleziona modalità" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_salone">In salone</SelectItem>
                          <SelectItem value="telefonica">Telefonica</SelectItem>
                          <SelectItem value="modulo_cartaceo">Modulo cartaceo</SelectItem>
                          <SelectItem value="modulo_digitale">Modulo digitale</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <p className="text-[11px] leading-relaxed text-muted-foreground" data-testid="new-appt-consent-note">
                    La scelta viene registrata sulla <b>scheda cliente</b> (permanente) con data, ora e operatore corrente. Negli appuntamenti successivi non verrà più richiesta.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveConsents} disabled={consentSaving || (!consentDraft.privacy_delivered && consentDraft.marketing === "unset")} data-testid="consent-save-button">{consentSaving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Salva consensi</Button>
                    {clientConsents?.complete && <Button size="sm" variant="ghost" onClick={() => { setConsentEditing(false); loadConsents(newClient.id); }} data-testid="consent-cancel-button">Annulla</Button>}
                  </div>
                </div>
              )}
            </div>

            <Button className="w-full" onClick={createAppt} data-testid="new-appt-save">Crea appuntamento</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setChangingClient(false); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto hi-scroll">
          {selected && (<>
            <DialogHeader><DialogTitle className="font-heading text-2xl flex items-center gap-2">{selected.client_name}{selected.client_id && debtSet.has(selected.client_id) && <span className="inline-flex items-center gap-1 text-xs font-medium rounded-full border border-[hsl(38_92%_45%/0.5)] bg-[hsl(38_92%_50%/0.12)] text-[hsl(38_92%_35%)] px-2 py-0.5" data-testid="appt-insoluto-indicator"><AlertTriangle className="h-3 w-3" /> Insoluto</span>}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Cliente</span>
                  {!changingClient && (
                    <div className="flex items-center gap-1">
                      {selected.client_id && (
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-[hsl(var(--hi-gold))]" onClick={() => navigate(`/clienti/${selected.client_id}?from=agenda&appt=${selected.id}&date=${romeDayKey(selected.start)}`)} data-testid="appt-edit-client-profile"><Pencil className="h-3 w-3 mr-1" /> Modifica profilo</Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setChangingClient(true)} data-testid="appt-change-client">Cambia cliente</Button>
                    </div>
                  )}
                </div>
                {changingClient
                  ? <ClientSearch value={null} onSelect={(c) => changeClient(c)} />
                  : <div className="font-medium">{selected.client_name}</div>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label>Data</Label>
                  <Input type="date" value={editForm?.date || ""} onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))} data-testid="appt-edit-date" />
                </div>
                <div className="space-y-1.5"><Label>Ora inizio</Label>
                  <Input type="time" step="300" value={editForm?.time || ""} onChange={(e) => setEditForm((f) => ({ ...f, time: e.target.value }))} data-testid="appt-edit-time" />
                </div>
              </div>
              <div className="space-y-1.5"><Label>Operatore</Label>
                <Select value={editForm?.operator_id || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, operator_id: v }))}>
                  <SelectTrigger data-testid="appt-edit-operator"><SelectValue placeholder="Operatore" /></SelectTrigger>
                  <SelectContent>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Servizi</Label>
                <div className="max-h-40 overflow-y-auto hi-scroll rounded-md border border-border p-1.5">
                  {services.map((s) => {
                    const on = (editForm?.service_ids || []).includes(s.id);
                    const qty = editForm?.service_qty?.[s.id] || 1;
                    return (
                      <div key={s.id} data-testid={`appt-edit-service-row-${s.id}`}
                        className={`w-full flex justify-between items-center gap-2 text-sm rounded px-2 py-1.5 ${on ? "bg-[hsl(var(--hi-gold-soft))]" : "hover:bg-accent"}`}>
                        <button type="button" data-testid={`appt-edit-service-${s.id}`} onClick={() => toggleEditService(s.id)}
                          className="flex-1 flex justify-between items-center text-left min-w-0">
                          <span className="truncate">{s.name}</span><span className="text-muted-foreground ml-2 shrink-0">{s.price_from ? "da " : ""}{eur(s.price)}</span>
                        </button>
                        {on && (
                          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <span className="text-xs text-muted-foreground">Qtà</span>
                            <Input type="number" min="1" value={qty} onChange={(e) => setEditQty(s.id, e.target.value)}
                              className="w-14 h-7 text-center px-1" data-testid={`appt-edit-qty-${s.id}`} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">Durata e prezzo (inclusi i servizi obbligatori) vengono ricalcolati al salvataggio. Aumenta la quantità per ripetere lo stesso servizio (es. Piega ×2).</p>
              </div>
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Prezzo attuale</span><span className="font-medium">{eur(selected.price)} · {selected.duration_min}′</span></div>
              <div className="space-y-1.5"><Label>Prezzo previsto <span className="text-muted-foreground font-normal">(facoltativo)</span></Label>
                <Input type="number" min="0" step="0.01" value={editForm?.expected_price || ""} onChange={(e) => setEditForm((f) => ({ ...f, expected_price: e.target.value }))} placeholder="Per servizi a prezzo variabile" data-testid="appt-edit-expected-price" />
              </div>
              <div className="space-y-1.5"><Label>Note</Label><Textarea value={editForm?.notes || ""} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} data-testid="appt-edit-notes" /></div>
              <Button className="w-full" onClick={saveEdit} data-testid="appt-edit-save">Salva modifiche</Button>

              {/* Assegnazione istanza servizio ad un'altra cliente (es. la figlia).
                  Utile per prese appuntamento rapide: il fatturato resta a {intestatario}. */}
              {(() => {
                const units = (selected.resolved_lines || []).flatMap((l) => {
                  const q = l.quantity || 1;
                  return Array.from({ length: q }, (_, i) => ({ service_id: l.service_id, name: l.name, unit: i, showNum: q > 1 }));
                });
                if (units.length === 0) return null;
                return (
                  <div className="space-y-2 rounded-lg border border-border p-3" data-testid="appt-assignments">
                    <div className="flex items-center gap-1.5"><Users className="h-4 w-4 text-[hsl(var(--hi-gold))]" /><Label>Assegna servizi a un'altra cliente</Label></div>
                    <p className="text-[11px] text-muted-foreground">Facoltativo. Assegna una singola prestazione a un'altra persona (es. la figlia) senza creare un nuovo appuntamento. Il fatturato resta a {selected.client_name}.</p>
                    <div className="space-y-1.5">
                      {units.map((u) => {
                        const key = `${u.service_id}#${u.unit}`;
                        const a = getAssignment(u.service_id, u.unit);
                        const editing = assignEditing === key;
                        return (
                          <div key={key} className="rounded-md border border-border/70 px-2.5 py-2" data-testid={`assign-unit-${key}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm truncate">{u.name}{u.showNum ? ` · #${u.unit + 1}` : ""}</span>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {a ? (
                                  <>
                                    <Badge variant="secondary" className="text-[10px]" data-testid={`assign-badge-${key}`}>{a.client_name}</Badge>
                                    <Button variant="ghost" size="icon" className="h-6 w-6" title="Rimuovi assegnazione" onClick={() => setAssignment(u.service_id, u.unit, null)} data-testid={`assign-remove-${key}`}><X className="h-3.5 w-3.5" /></Button>
                                  </>
                                ) : (
                                  <span className="text-[11px] text-muted-foreground">{selected.client_name}</span>
                                )}
                                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-[hsl(var(--hi-gold))]" onClick={() => setAssignEditing(editing ? null : key)} data-testid={`assign-toggle-${key}`}>{editing ? "Annulla" : (a ? "Cambia" : "Assegna")}</Button>
                              </div>
                            </div>
                            {editing && (
                              <div className="mt-2" data-testid={`assign-search-${key}`}>
                                <ClientSearch value={null} onSelect={(c) => c && setAssignment(u.service_id, u.unit, c)} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <div className="flex items-center justify-between pt-1"><span className="text-sm text-muted-foreground">Stato</span><StatusBadge status={selected.status} /></div>
              <div className="space-y-1.5"><Label>Cambia stato</Label>
                <Select value={selected.status} onValueChange={(v) => changeStatus(selected, v)}>
                  <SelectTrigger data-testid="appt-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(APPT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5 rounded-lg border border-border p-3" data-testid="appt-op-status">
                <div className="flex items-center justify-between">
                  <Label>Avanzamento servizio</Label>
                  {selected.op_status && (
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
                      style={{ color: `hsl(var(${OP_STATUS[selected.op_status]?.token}))`, backgroundColor: `hsl(var(${OP_STATUS[selected.op_status]?.token}) / 0.12)`, border: `1px solid hsl(var(${OP_STATUS[selected.op_status]?.token}) / 0.3)` }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: `hsl(var(${OP_STATUS[selected.op_status]?.token}))` }} />
                      {OP_STATUS[selected.op_status]?.label}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {OP_STATUS_ORDER.map((k) => {
                    const active = selected.op_status === k;
                    return (
                      <button key={k} onClick={() => changeOpStatus(selected, k)} data-testid={`op-status-${k}`}
                        className="rounded-full px-2.5 py-1 text-xs font-medium border transition-colors"
                        style={active
                          ? { color: `hsl(var(${OP_STATUS[k].token}))`, backgroundColor: `hsl(var(${OP_STATUS[k].token}) / 0.15)`, borderColor: `hsl(var(${OP_STATUS[k].token}) / 0.5)` }
                          : { color: "hsl(var(--muted-foreground))", borderColor: "hsl(var(--border))" }}>
                        {OP_STATUS[k].short}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2 rounded-lg border border-border p-3" data-testid="appt-timers">
                <Label>Timer servizio</Label>
                <div className="flex flex-wrap gap-1.5">
                  {TIMER_PRESETS.map((p) => (
                    <Button key={p.label} variant="outline" size="sm" className="h-7 text-xs" onClick={() => startTimer(selected, p.label, p.minutes)} data-testid={`timer-preset-${p.label}`}>
                      {p.label} {p.minutes}'
                    </Button>
                  ))}
                </div>
                {(selected.timers || []).length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    {(selected.timers || []).map((t) => {
                      const remMs = new Date(t.ends_at).getTime() - Date.now();
                      const done = remMs <= 0;
                      const mm = Math.floor(Math.abs(remMs) / 60000);
                      const ss = Math.floor((Math.abs(remMs) % 60000) / 1000);
                      return (
                        <div key={t.id} className={`flex items-center justify-between rounded-md px-2.5 py-1.5 text-sm ${done ? "bg-destructive/10" : "bg-muted"}`} data-testid={`timer-row-${t.id}`}>
                          <span className="flex items-center gap-2">
                            <Timer className={`h-4 w-4 ${done ? "text-destructive" : "text-[hsl(var(--hi-gold))]"}`} />
                            <span className="font-medium">{t.label}</span>
                          </span>
                          <span className="flex items-center gap-2">
                            <span className={`font-mono tabular-nums ${done ? "text-destructive font-semibold" : ""}`}>{done ? "+" : ""}{String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeTimer(selected, t.id)}><X className="h-3.5 w-3.5" /></Button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <ColorFormulaPanel appointmentId={selected.id} />

              {selected.status !== "completed" && (
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => openQuickPay(selected)} data-testid="appt-quickpay-button">
                    <Wallet className="h-4 w-4 mr-1" /> Paga ora
                  </Button>
                  <Button variant="outline" onClick={() => goCheckout(selected)} data-testid="appt-checkout-button">
                    Apri in Cassa
                  </Button>
                </div>
              )}
              {selected.status === "completed" && selected.sale_id && (
                <div className="text-sm text-center text-[hsl(var(--hi-status-success))] font-medium" data-testid="appt-already-paid">Appuntamento già incassato</div>
              )}
              <Button variant="destructive" className="w-full" onClick={() => removeAppt(selected)} data-testid="appt-delete"><Trash2 className="h-4 w-4 mr-1" /> Elimina appuntamento</Button>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Pagamento rapido dall'appuntamento */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md" data-testid="quickpay-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Paga appuntamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-border divide-y divide-border">
              {payLines.map((l, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm">
                  <span className="truncate">{l.quantity > 1 ? `${l.quantity}× ` : ""}{l.name}</span>
                  <span className="font-medium">€ {((Number(l.unit_price) || 0) * (l.quantity || 1)).toFixed(2)}</span>
                </div>
              ))}
              {payLines.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Nessun servizio associato</div>}
            </div>
            <div className="flex items-center justify-between text-sm font-semibold">
              <span>Totale</span><span data-testid="quickpay-total">€ {payTotal.toFixed(2)}</span>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Pagamenti (contanti / POS / gift / misto)</Label>
              {payments.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <Select value={p.method} onValueChange={(v) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, method: v } : x))}>
                    <SelectTrigger className="flex-1" data-testid={`quickpay-method-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" value={p.amount} placeholder="0.00" className="w-28" data-testid={`quickpay-amount-${i}`}
                    onChange={(e) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} />
                  {payments.length > 1 && <Button variant="ghost" size="icon" onClick={() => setPayments((ps) => ps.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>}
                </div>
              ))}
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => setPayments((ps) => [...ps, { method: "Carta", amount: "" }])} data-testid="quickpay-add-method"><Plus className="h-4 w-4 mr-1" />Aggiungi metodo</Button>
                <span className={`text-xs ${Math.abs(paySum - payTotal) < 0.01 ? "text-[hsl(var(--hi-status-success))]" : "text-muted-foreground"}`} data-testid="quickpay-sum">Incassato: € {paySum.toFixed(2)}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button variant="outline" onClick={() => submitQuickPay(true)} disabled={payBusy} data-testid="quickpay-unpaid">Salva non pagato</Button>
              <Button onClick={() => submitQuickPay(false)} disabled={payBusy || paySum <= 0} data-testid="quickpay-confirm">{payBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma pagamento"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pannello operatore (Modulo Agenda Operatore) */}
      <OperatorDrawer
        open={opDrawer.open}
        onOpenChange={(o) => setOpDrawer((d) => ({ ...d, open: o }))}
        operatorId={opDrawer.operatorId}
        date={date}
        onNewAppointment={(opId) => quickCreate(opId, "10:00")}
        onSelectAppointment={selectApptById}
        onChanged={load}
      />
    </div>
  );
}

function ApptBlock({ a, onSelect, compact, onDragStartAppt, lane = 0, lanes = 1, color = "43 45% 54%", hasDebt = false }) {
  const top = (romeMinutes(a.start) - DAY_START) * PX_PER_MIN;
  const height = Math.max(28, (a.duration_min || 30) * PX_PER_MIN);
  const widthPct = 100 / lanes;
  const left = `calc(${lane * widthPct}% + 2px)`;
  const width = `calc(${widthPct}% - 4px)`;
  const faded = a.status === "cancelled" || a.status === "no_show";
  // Suddivisione in segmenti per servizio (uno per riga, con la propria durata e categoria).
  const rl = (a.resolved_lines && a.resolved_lines.length) ? a.resolved_lines : null;
  let segs = rl
    ? rl.map((l) => ({ name: l.name, category: l.category, min: ((l.duration_min || 0) + (l.processing_min || 0)) * (l.quantity || 1) }))
    : (a.service_names || []).map((n) => ({ name: n, category: null, min: 0 }));
  segs = segs.filter((s) => s.name);
  const dur = a.duration_min || 30;
  const totalMin = segs.reduce((x, s) => x + s.min, 0);
  if (totalMin <= 0 && segs.length) { const each = dur / segs.length; segs = segs.map((s) => ({ ...s, min: each })); }
  const multi = segs.length > 1 && !compact;
  const base = new Date(a.start).getTime();
  let acc = 0;
  const segViews = segs.map((s) => { const st = base + acc * 60000; acc += s.min; const en = base + acc * 60000; return { name: s.name, min: s.min, start: new Date(st).toISOString(), end: new Date(en).toISOString(), color: s.category ? categoryColor(s.category) : color }; });
  const timerBadge = (a.timers || []).length > 0 ? (() => {
    const next = [...a.timers].sort((x, y) => new Date(x.ends_at) - new Date(y.ends_at))[0];
    const remMs = new Date(next.ends_at).getTime() - Date.now();
    const done = remMs <= 0;
    const mm = Math.floor(Math.abs(remMs) / 60000);
    const ss = Math.floor((Math.abs(remMs) % 60000) / 1000);
    return (
      <div className={`absolute top-1 right-1 z-10 inline-flex items-center gap-1 rounded px-1 text-[10px] font-mono font-semibold ${done ? "bg-destructive text-destructive-foreground animate-pulse" : "bg-[hsl(var(--hi-gold)/0.2)] text-[hsl(var(--hi-gold))]"}`} data-testid={`block-timer-${a.id}`}>
        <Timer className="h-2.5 w-2.5" />{done ? "FINE " : ""}{String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
      </div>
    );
  })() : null;
  return (
    <button onClick={(e) => { e.stopPropagation(); onSelect(a); }} data-testid="appointment-card"
      draggable={!!onDragStartAppt}
      onTouchStart={(e) => e.stopPropagation()}
      onDragStart={(e) => onDragStartAppt && onDragStartAppt(e, a)}
      className={`absolute z-[2] rounded-lg border bg-card text-left overflow-hidden shadow-sm hover:z-10 cursor-grab active:cursor-grabbing ${faded ? "opacity-60" : ""}`}
      style={{ top, height, left, width, borderLeft: `4px solid hsl(${multi ? segViews[0].color : color})` }}>
      {!multi && <span aria-hidden className="absolute inset-0 pointer-events-none" style={{ backgroundColor: `hsl(${color} / 0.14)` }} />}
      {timerBadge}
      {multi ? (
        <div className="relative flex flex-col h-full">
          {segViews.map((sv, i) => (
            <div key={i} className={`relative pl-2 pr-2 py-1 overflow-hidden ${i > 0 ? "border-t border-border/70" : ""}`}
              style={{ height: Math.max(18, sv.min * PX_PER_MIN), backgroundColor: `hsl(${sv.color} / 0.16)` }} data-testid={`block-seg-${a.id}-${i}`}>
              <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: `hsl(${sv.color})` }} />
              <div className="text-[11px] font-semibold leading-tight truncate flex items-center gap-1 pl-1.5">
                {i === 0 && a.op_status && <span className="h-2 w-2 rounded-full shrink-0" title={OP_STATUS[a.op_status]?.label} style={{ backgroundColor: `hsl(var(${OP_STATUS[a.op_status]?.token}))` }} data-testid={`block-op-dot-${a.id}`} />}
                {i === 0 && hasDebt && <AlertTriangle className="h-3 w-3 shrink-0 text-[hsl(38_92%_42%)]" data-testid="agenda-insoluto-indicator" />}
                <span className="truncate">{fmtTime(sv.start)}–{fmtTime(sv.end)}{i === 0 ? ` · ${a.client_name}` : ""}</span>
              </div>
              <div className="text-[10px] truncate leading-tight pl-1.5" style={{ color: `hsl(${sv.color})` }}>{sv.name}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="relative p-2">
          <div className="text-xs font-semibold truncate flex items-center gap-1">
            {a.op_status && <span className="h-2 w-2 rounded-full shrink-0" title={OP_STATUS[a.op_status]?.label} style={{ backgroundColor: `hsl(var(${OP_STATUS[a.op_status]?.token}))` }} data-testid={`block-op-dot-${a.id}`} />}
            {hasDebt && <AlertTriangle className="h-3 w-3 shrink-0 text-[hsl(38_92%_42%)]" data-testid="agenda-insoluto-indicator" />}
            <span className="truncate">{fmtTime(a.start)} · {a.client_name}</span>
          </div>
          {!compact && <div className="text-[11px] text-muted-foreground truncate">{(a.service_names||[]).join(", ")}</div>}
          {!compact && a.op_status && a.op_status !== "waiting" && (
            <div className="text-[10px] font-medium truncate" style={{ color: `hsl(var(${OP_STATUS[a.op_status]?.token}))` }}>{OP_STATUS[a.op_status]?.label}</div>
          )}
        </div>
      )}
    </button>
  );
}

function DayView({ team, appts, onSelect, date, now, onQuickCreate, onMoveAppt, onOperatorClick, availability, svcCat, debtSet }) {
  const isToday = romeDayKey(now) === romeDayKey(date);
  const nowMin = romeMinutes(now);
  const gridMax = DAY_START + HOURS.length * 60;
  const showNow = isToday && nowMin >= DAY_START && nowMin <= gridMax;
  const nowTop = (nowMin - DAY_START) * PX_PER_MIN;
  const nowLabel = `${String(Math.floor(nowMin / 60)).padStart(2, "0")}:${String(nowMin % 60).padStart(2, "0")}`;

  // Auto-scroll all'apertura: posiziona su ora corrente (oggi) o sul primo appuntamento.
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      let targetMin;
      if (isToday) {
        targetMin = nowMin;
      } else {
        const mins = appts
          .filter((a) => romeDayKey(a.start) === romeDayKey(date))
          .map((a) => romeMinutes(a.start));
        targetMin = mins.length ? Math.min(...mins) : 9 * 60;
      }
      el.scrollTop = Math.max(0, (targetMin - DAY_START) * PX_PER_MIN - 90);
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, appts.length, isToday]);

  const salon = availability?.salon;
  const opAvail = availability?.operators || {};
  const hm2m = (hm) => { const [h, m] = String(hm).split(":"); return (+h) * 60 + (+m); };
  // Bande "chiuse" (complemento delle fasce disponibili) entro la griglia visibile
  const closedBands = (ranges) => {
    const rs = (ranges || []).map((r) => [Math.max(DAY_START, hm2m(r[0])), Math.min(gridMax, hm2m(r[1]))]).filter(([s, e]) => e > s).sort((a, b) => a[0] - b[0]);
    const bands = []; let cur = DAY_START;
    for (const [s, e] of rs) { if (s > cur) bands.push([cur, s]); cur = Math.max(cur, e); }
    if (cur < gridMax) bands.push([cur, gridMax]);
    return bands;
  };
  const isInRanges = (mins, ranges) => (ranges || []).some((r) => hm2m(r[0]) <= mins && mins < hm2m(r[1]));

  const minutesFromClientY = (el, clientY) => {
    const rect = el.getBoundingClientRect();
    const y = clientY - rect.top;
    let mins = DAY_START + Math.round((y / PX_PER_MIN) / 15) * 15;
    mins = Math.max(DAY_START, Math.min(gridMax - 15, mins));
    return mins;
  };
  const minutesFromY = (e) => minutesFromClientY(e.currentTarget, e.clientY);
  const toHHMM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

  // Long-press mobile: crea appuntamento tenendo premuto (evita creazioni accidentali
  // durante lo scroll). Il tap semplice su touch NON crea (solo lo scroll/drag).
  const lpTimer = useRef(null);
  const lpSuppressClick = useRef(false);
  const lpStartY = useRef(0);
  const clearLp = () => { if (lpTimer.current) { clearTimeout(lpTimer.current); lpTimer.current = null; } };
  const startLongPress = (el, t, av, ranges) => (e) => {
    const touch = e.touches && e.touches[0]; if (!touch) return;
    lpStartY.current = touch.clientY;
    clearLp();
    lpTimer.current = setTimeout(() => {
      lpTimer.current = null;
      lpSuppressClick.current = true; // impedisce il click sintetico successivo
      const mins = minutesFromClientY(el, lpStartY.current);
      if (av && !isInRanges(mins, ranges)) { toast.warning(av.reason || "Fascia non disponibile"); return; }
      try { if (navigator.vibrate) navigator.vibrate(15); } catch (_) {}
      onQuickCreate && onQuickCreate(t.id, toHHMM(mins));
    }, 450);
  };
  const moveLongPress = (e) => {
    const touch = e.touches && e.touches[0]; if (!touch) return;
    if (Math.abs(touch.clientY - lpStartY.current) > 8) clearLp(); // sta scrollando
  };

  const onDragStartAppt = (e, a) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", a.id);
  };

  return (
    <Card className="p-0 overflow-hidden">
      {salon && !salon.open && (
        <div className="flex items-center gap-2 px-4 py-2 bg-destructive/10 border-b border-destructive/30 text-sm text-destructive" data-testid="agenda-closed-banner">
          <Lock className="h-4 w-4" /> Salone chiuso{salon.reason ? ` · ${salon.reason}` : ""}
        </div>
      )}
      {salon && salon.open && salon.exception_type && (
        <div className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--hi-gold-soft))] border-b border-border text-sm" data-testid="agenda-exception-banner">
          <Clock3 className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> {salon.reason || "Orario speciale"} · {(salon.ranges || []).map((r) => `${r[0]}-${r[1]}`).join(", ")}
        </div>
      )}
      {(() => {
        // Legenda consultabile: mostra TUTTE le categorie configurate (colore condiviso
        // con la Cassa) + eventuali categorie presenti nel giorno non ancora configurate.
        const ordered = Object.keys(CAT_COLOR_MAP);
        const catSet = new Set(ordered);
        appts.forEach((a) => {
          if (a.resolved_lines && a.resolved_lines.length) {
            a.resolved_lines.forEach((l) => { if (l.category) catSet.add(l.category); });
          } else {
            (a.service_ids || []).forEach((sid) => { const cc = svcCat && svcCat[sid]; if (cc) catSet.add(cc); });
          }
        });
        const extra = Array.from(catSet).filter((c) => !ordered.includes(c)).sort();
        const legendCats = [...ordered, ...extra];
        if (!legendCats.length) return null;
        return (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 border-b border-border" data-testid="agenda-category-legend">
            <span className="text-[11px] font-medium text-muted-foreground">Legenda categorie:</span>
            {legendCats.map((cat) => (
              <span key={cat} className="inline-flex items-center gap-1.5 text-[11px] text-foreground/80" data-testid={`legend-${cat}`}>
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: `hsl(${categoryColor(cat)})` }} />
                {cat}
              </span>
            ))}
          </div>
        );
      })()}
      <div ref={scrollRef} className="overflow-auto hi-scroll" style={{ maxHeight: "calc(100vh - 240px)", minHeight: 420 }}>
        <div className="flex min-w-[720px]" style={{ minHeight: 52 + HOURS.length * 60 * PX_PER_MIN }}>
          <div className="w-14 shrink-0 border-r border-border relative">
            <div className="h-[52px] sticky top-0 bg-card z-20 border-b border-border" />
            {HOURS.map((h) => <div key={h} className="text-[11px] text-muted-foreground text-right pr-2" style={{ height: 60 * PX_PER_MIN }}>{String(h).padStart(2, "0")}:00</div>)}
            {showNow && (
              <div className="absolute right-0 z-30" style={{ top: 52 + nowTop - 8 }} data-testid="agenda-now-label">
                <span className="text-[10px] font-semibold text-white bg-destructive rounded px-1 py-0.5">{nowLabel}</span>
              </div>
            )}
          </div>
          <div className="flex-1 flex">
            {team.map((t) => {
              const list = appts.filter((a) => a.operator_id === t.id);
              const laneMap = computeLanes(list);
              const av = opAvail[t.id];
              const ranges = av?.ranges || [];
              const unavailable = av ? !av.available : false;
              const bands = av ? closedBands(ranges) : [];
              return (
                <div key={t.id} className="flex-1 min-w-[150px] border-r border-border relative">
                  <button type="button" onClick={() => onOperatorClick && onOperatorClick(t.id)}
                    className="h-[52px] w-full flex items-center gap-2 px-2 border-b border-border sticky top-0 bg-card z-10 text-left hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hi-gold))]"
                    data-testid={`operator-header-${t.id}`} title={`Apri pannello ${t.name}`}>
                    <Avatar className="h-7 w-7"><AvatarFallback className="text-[10px]" style={{ background: (t.color||"#C9A24B")+"22", color: t.color }}>{initials(t.name)}</AvatarFallback></Avatar>
                    <div className="text-xs font-medium truncate">{t.name}</div>
                  </button>
                  <div className="relative cursor-pointer" style={{ height: HOURS.length * 60 * PX_PER_MIN }}
                    data-testid={`agenda-column-${t.id}`}
                    onClick={(e) => { if (lpSuppressClick.current) { lpSuppressClick.current = false; return; } const mins = minutesFromY(e); if (av && !isInRanges(mins, ranges)) { toast.warning(av.reason || "Fascia non disponibile"); return; } onQuickCreate && onQuickCreate(t.id, toHHMM(mins)); }}
                    onTouchStart={(e) => startLongPress(e.currentTarget, t, av, ranges)(e)}
                    onTouchMove={moveLongPress}
                    onTouchEnd={() => { clearLp(); lpSuppressClick.current = true; }}
                    onTouchCancel={() => { clearLp(); lpSuppressClick.current = true; }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                    onDrop={(e) => { e.preventDefault(); const mins = minutesFromY(e); if (av && !isInRanges(mins, ranges)) { toast.warning(av.reason || "Fascia non disponibile"); return; } const id = e.dataTransfer.getData("text/plain"); if (id && onMoveAppt) onMoveAppt(id, t.id, toHHMM(mins)); }}>
                    {HOURS.map((h, i) => <div key={h} className="absolute left-0 right-0 border-b border-border/50" style={{ top: i * 60 * PX_PER_MIN }} />)}
                    {/* Fasce chiuse/fuori turno attenuate */}
                    {bands.map(([s, e], bi) => (
                      <div key={bi} className="absolute left-0 right-0 hi-closed-band pointer-events-none z-[1]" style={{ top: (s - DAY_START) * PX_PER_MIN, height: (e - s) * PX_PER_MIN }} data-testid={`agenda-closed-band-${t.id}-${bi}`} />
                    ))}
                    {unavailable && (
                      <div className="absolute inset-x-0 top-2 flex justify-center pointer-events-none z-[2]">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">{av?.reason || "Non disponibile"}</span>
                      </div>
                    )}
                    {showNow && <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: nowTop }} data-testid="agenda-now-line"><div className="relative"><span className="absolute -left-1 -top-[3px] h-2 w-2 rounded-full bg-destructive" /><div className="h-[2px] bg-destructive/80" /></div></div>}
                    {list.map((a) => { const lp = laneMap.get(a.id) || { lane: 0, lanes: 1 }; return <ApptBlock key={a.id} a={a} onSelect={onSelect} onDragStartAppt={onDragStartAppt} lane={lp.lane} lanes={lp.lanes} color={categoryColor(apptCategory(a, svcCat))} hasDebt={debtSet?.has(a.client_id)} />; })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}

function WeekView({ range, appts, onSelect, now, svcCat, debtSet }) {
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(range.s); d.setDate(d.getDate() + i); return d; });
  const nowLabel = `${String(Math.floor(romeMinutes(now) / 60)).padStart(2, "0")}:${String(romeMinutes(now) % 60).padStart(2, "0")}`;
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
      {days.map((d) => {
        const isToday = romeDayKey(d) === romeDayKey(now);
        const list = appts.filter((a) => romeDayKey(a.start) === romeDayKey(d)).sort((a,b)=>a.start.localeCompare(b.start));
        return (
          <Card key={d.toISOString()} className={`p-3 ${isToday ? "ring-2 ring-[hsl(var(--hi-gold))]" : ""}`} data-testid={isToday ? "week-today-card" : undefined}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground capitalize">{fmtDate(d, { weekday: "short", day: "2-digit" })}</div>
              {isToday && <span className="text-[10px] font-semibold text-white bg-destructive rounded px-1.5 py-0.5">Adesso {nowLabel}</span>}
            </div>
            <div className="space-y-2">
              {list.length === 0 && <div className="text-[11px] text-muted-foreground">—</div>}
              {list.map((a) => {
                const color = categoryColor(apptCategory(a, svcCat));
                const faded = a.status === "cancelled" || a.status === "no_show";
                return <button key={a.id} onClick={() => onSelect(a)} data-testid="appointment-card" className={`w-full text-left rounded-lg border border-border p-2 hover:bg-accent ${faded ? "opacity-60" : ""}`} style={{ borderLeft: `4px solid hsl(${color})`, backgroundColor: `hsl(${color} / 0.10)` }}>
                  <div className="text-xs font-semibold">{fmtTime(a.start)}</div>
                  <div className="text-xs truncate">{debtSet?.has(a.client_id) && <AlertTriangle className="inline h-3 w-3 mr-0.5 -mt-0.5 text-[hsl(38_92%_42%)]" data-testid="agenda-insoluto-indicator" />}{a.client_name}</div>
                </button>;
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function MonthView({ date, appts, onSelect, svcCat, debtSet }) {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(date.getFullYear(), date.getMonth(), d));
  return (
    <Card className="p-3">
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
        {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} />;
          const list = appts.filter((a) => romeDayKey(a.start) === romeDayKey(c));
          return (
            <div key={i} className="min-h-[92px] rounded-lg border border-border p-1.5">
              <div className="text-xs font-medium mb-1">{c.getDate()}</div>
              <div className="space-y-0.5">
                {list.slice(0, 3).map((a) => {
                  const color = categoryColor(apptCategory(a, svcCat));
                  return <button key={a.id} onClick={() => onSelect(a)} data-testid="appointment-card" className="w-full text-left text-[10px] truncate rounded px-1 py-0.5 font-medium" style={{ background: `hsl(${color} / 0.16)`, color: `hsl(${color})` }}>{debtSet?.has(a.client_id) && <AlertTriangle className="inline h-2.5 w-2.5 mr-0.5 -mt-0.5 text-[hsl(38_92%_42%)]" />}{fmtTime(a.start)} {a.client_name}</button>;
                })}
                {list.length > 3 && <div className="text-[10px] text-muted-foreground">+{list.length - 3}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
